import { useState, useEffect } from 'react';
import { getStoreAllowedItemClasses } from '../api/stores';

/**
 * A store's item-class restriction, for filtering item pickers.
 *
 * `isRestricted` false (the default, and the case for every store that predates
 * the feature) means no filtering — callers must send no class param at all.
 *
 * Fails open: on any error this reports "unrestricted" so a blip in the policy
 * lookup never blocks data entry. The server enforces authoritatively, so the
 * worst case is a picker showing an item the write path will reject.
 */

const UNRESTRICTED = { classIds: [], isRestricted: false };

// Module-level cache of in-flight/settled lookups, keyed by store id. Line-item
// editors render one picker per row, so without this a 20-line document would
// fire 20 identical requests.
const cache = new Map();

function fetchAllowedItemClasses(storeId) {
  if (cache.has(storeId)) return cache.get(storeId);

  const promise = getStoreAllowedItemClasses(storeId)
    .then((data) => {
      const ids = Array.isArray(data?.allowed_item_class_ids)
        ? data.allowed_item_class_ids.map(String).filter(Boolean)
        : [];
      return { classIds: ids, isRestricted: !!data?.is_restricted && ids.length > 0 };
    })
    .catch(() => {
      // Do not cache a failure — a later render should be able to retry.
      cache.delete(storeId);
      return UNRESTRICTED;
    });

  cache.set(storeId, promise);
  return promise;
}

/** Drop a cached entry after the store's allow-list is edited. */
export function invalidateStoreItemClasses(storeId) {
  if (storeId) cache.delete(storeId);
  else cache.clear();
}

export default function useStoreItemClasses(storeId) {
  const [state, setState] = useState({ ...UNRESTRICTED, loading: false });

  useEffect(() => {
    if (!storeId) {
      setState({ ...UNRESTRICTED, loading: false });
      return undefined;
    }

    let active = true;
    setState((prev) => ({ ...prev, loading: true }));
    fetchAllowedItemClasses(storeId).then((result) => {
      if (active) setState({ ...result, loading: false });
    });

    return () => { active = false; };
  }, [storeId]);

  return state;
}
